import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const BRAND = {
  navy: [11, 17, 32],
  blue: [36, 48, 122],
  slate: [71, 85, 105],
  light: [248, 250, 252],
  border: [226, 232, 240],
  green: [22, 101, 52],
  red: [153, 27, 27],
  amber: [133, 77, 14],
};

const safe = (value, fallback = "SIN DATO") => {
  const v = String(value ?? "").trim();
  return v || fallback;
};

const normalize = (value) =>
  String(value ?? "")
    .trim()
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const getEquipo = (item) => safe(item.equipo || item.tipoEquipo || item.tipo || item.nombreEquipo);
const getMarca = (item) => safe(item.marca, "");
const getModelo = (item) => safe(item.modelo, "");
const getSerie = (item) => safe(item.serie || item.serial, "");
const getEstado = (item) => safe(item.estadoEquipo || item.estado || item.estatusEquipo, "SIN ESTADO");
const getProyecto = (item) => safe(item.proyecto, "SIN PROYECTO");
const getMunicipio = (item) => safe(item.municipio, "");
const getOficina = (item) => safe(item.oficina, "");
const getModulo = (item) => safe(item.modulo || item.estacion || item.moduloAsignado, "");
const getNotas = (item) => safe(item.notas || item.observaciones, "");
const getId = (item) => safe(item.id || item.idEquipo || item.pk, "");

function esStock(item) {
  const estatusAsignacion = normalize(
    item.estatusAsignacion || item.asignacion || item.tipoAsignacion || ""
  );

  const municipio = getMunicipio(item);
  const oficina = getOficina(item);
  const modulo = getModulo(item);
  const proyecto = getProyecto(item);

  return (
    estatusAsignacion === "STOCK" ||
    estatusAsignacion === "SIN ASIGNAR" ||
    estatusAsignacion === "DISPONIBLE" ||
    proyecto === "SIN PROYECTO" ||
    municipio === "" ||
    oficina === "" ||
    modulo === ""
  );
}

function agruparInventarioEnterprise(data = []) {
  const asignado = {};
  const stock = [];

  data.forEach((item) => {
    if (esStock(item)) {
      stock.push(item);
      return;
    }

    const proyecto = getProyecto(item);
    const municipio = getMunicipio(item);
    const oficina = getOficina(item);
    const modulo = getModulo(item);

    if (!asignado[proyecto]) asignado[proyecto] = {};
    if (!asignado[proyecto][municipio]) asignado[proyecto][municipio] = {};
    if (!asignado[proyecto][municipio][oficina]) asignado[proyecto][municipio][oficina] = {};
    if (!asignado[proyecto][municipio][oficina][modulo]) {
      asignado[proyecto][municipio][oficina][modulo] = [];
    }

    asignado[proyecto][municipio][oficina][modulo].push(item);
  });

  return { asignado, stock };
}

function contarTotales(asignado, stock) {
  const proyectos = Object.keys(asignado).length;
  const municipiosSet = new Set();
  const oficinasSet = new Set();
  const estacionesSet = new Set();

  let asignados = 0;

  Object.entries(asignado).forEach(([proyecto, municipiosObj]) => {
    Object.entries(municipiosObj).forEach(([municipio, oficinasObj]) => {
      municipiosSet.add(municipio);

      Object.entries(oficinasObj).forEach(([oficina, estacionesObj]) => {
        // ✅ Oficina física única por municipio
        oficinasSet.add(`${municipio}|${oficina}`);

        Object.entries(estacionesObj).forEach(([estacion, equipos]) => {
          // ✅ Estación física única por municipio + oficina + módulo
          estacionesSet.add(`${municipio}|${oficina}|${estacion}`);
          asignados += equipos.length;
        });
      });
    });
  });

  return {
    total: asignados + stock.length,
    asignados,
    stock: stock.length,
    proyectos,
    municipios: municipiosSet.size,
    oficinas: oficinasSet.size,
    estaciones: estacionesSet.size,
  };
}

function resumenPorProyecto(asignado, stock) {
  const rows = Object.entries(asignado)
    .sort(([a], [b]) => a.localeCompare(b, "es"))
    .map(([proyecto, municipiosObj]) => {
      const municipiosSet = new Set();
      const oficinasSet = new Set();
      const estacionesSet = new Set();
      let equipos = 0;

      Object.entries(municipiosObj).forEach(([municipio, oficinasObj]) => {
        municipiosSet.add(municipio);

        Object.entries(oficinasObj).forEach(([oficina, estacionesObj]) => {
          oficinasSet.add(`${municipio}|${oficina}`);

          Object.entries(estacionesObj).forEach(([estacion, items]) => {
            estacionesSet.add(`${municipio}|${oficina}|${estacion}`);
            equipos += items.length;
          });
        });
      });

      return [
        proyecto,
        municipiosSet.size,
        oficinasSet.size,
        estacionesSet.size,
        equipos,
      ];
    });

  if (stock.length > 0) {
    rows.push(["STOCK / SIN ASIGNAR", "-", "-", "-", stock.length]);
  }

  return rows;
}

function resumenPorMunicipio(asignado) {
  const acc = {};

  Object.entries(asignado).forEach(([proyecto, municipiosObj]) => {
    Object.entries(municipiosObj).forEach(([municipio, oficinasObj]) => {
      if (!acc[municipio]) {
        acc[municipio] = {
          proyectos: new Set(),
          oficinas: new Set(),
          estaciones: new Set(),
          equipos: 0,
        };
      }

      acc[municipio].proyectos.add(proyecto);

      Object.entries(oficinasObj).forEach(([oficina, estacionesObj]) => {
        // ✅ Cuenta oficina física, no oficina por proyecto
        acc[municipio].oficinas.add(oficina);

        Object.entries(estacionesObj).forEach(([estacion, items]) => {
          // ✅ Cuenta estación/módulo físico por oficina
          acc[municipio].estaciones.add(`${oficina}|${estacion}`);
          acc[municipio].equipos += items.length;
        });
      });
    });
  });

  return Object.entries(acc)
    .sort(([a], [b]) => a.localeCompare(b, "es"))
    .map(([municipio, r]) => [
      municipio,
      r.proyectos.size,
      r.oficinas.size,
      r.estaciones.size,
      r.equipos,
    ]);
}

function evaluarEstacion(nombreModulo, items = []) {
  const modulo = normalize(nombreModulo);
  const nombres = items.map((i) => normalize(getEquipo(i))).join(" ");

  let requeridos = [];

  if (modulo.includes("DATO")) {
    requeridos = ["CPU", "MONITOR", "TECLADO", "MOUSE", "REGULADOR"];
  } else if (modulo.includes("BIOMETR")) {
    requeridos = [
      "CPU",
      "MONITOR",
      "TECLADO",
      "MOUSE",
      "REGULADOR",
      "HUELLA",
      "PAD",
      "FIRMA",
      "CAMARA",
      "ESCANER",
    ];
  } else {
    requeridos = ["CPU", "MONITOR", "TECLADO", "MOUSE"];
  }

  const faltantes = requeridos.filter((req) => !nombres.includes(req));

  if (faltantes.length === 0) {
    return {
      estatus: "COMPLETA",
      faltantes: "Sin faltantes críticos detectados",
    };
  }

  return {
    estatus: "REVISAR",
    faltantes: faltantes.join(", "),
  };
}

function estadoColor(estado) {
  const e = normalize(estado);

  if (e.includes("BUENO") || e.includes("ACTIVO") || e.includes("OPERATIVO")) {
    return BRAND.green;
  }

  if (e.includes("REGULAR") || e.includes("REVISION") || e.includes("REVISAR")) {
    return BRAND.amber;
  }

  if (e.includes("MALO") || e.includes("BAJA") || e.includes("NO OPERATIVO")) {
    return BRAND.red;
  }

  return BRAND.slate;
}

function drawHeader(doc, titulo, subtitulo = "") {
  doc.setFillColor(...BRAND.navy);
  doc.rect(0, 0, 297, 18, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(255, 255, 255);
  doc.text(titulo, 12, 11);

  if (subtitulo) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(220, 230, 240);
    doc.text(subtitulo, 284, 11, { align: "right" });
  }
}

function drawFooter(doc) {
  const totalPages = doc.getNumberOfPages();

  for (let i = 1; i <= totalPages; i += 1) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(110, 120, 135);
    doc.text(
      `IP DEPOT · Plano Maestro de Inventario · Página ${i} de ${totalPages}`,
      148.5,
      204,
      { align: "center" }
    );
  }
}

function drawKpi(doc, x, y, w, h, title, value) {
  doc.setDrawColor(...BRAND.border);
  doc.setFillColor(...BRAND.light);
  doc.roundedRect(x, y, w, h, 3, 3, "FD");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.setTextColor(...BRAND.slate);
  doc.text(title, x + 3, y + 6);

  doc.setFontSize(15);
  doc.setTextColor(...BRAND.navy);
  doc.text(String(value), x + 3, y + 15);
}

function addSectionTitle(doc, text, y) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(...BRAND.navy);
  doc.text(text, 12, y);

  doc.setDrawColor(...BRAND.blue);
  doc.line(12, y + 2, 285, y + 2);
}

function table(doc, config) {
  autoTable(doc, {
    theme: "grid",
    margin: { left: 10, right: 10 },
    styles: {
      fontSize: 7,
      cellPadding: 1.5,
      overflow: "linebreak",
      textColor: [31, 41, 55],
      lineColor: [226, 232, 240],
      lineWidth: 0.1,
    },
    headStyles: {
      fillColor: BRAND.navy,
      textColor: 255,
      fontStyle: "bold",
      fontSize: 7,
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252],
    },
    ...config,
  });
}

export function exportInventarioPlanoPdf(inventario = []) {
  const data = Array.isArray(inventario) ? inventario : [];
  const { asignado, stock } = agruparInventarioEnterprise(data);
  const totales = contarTotales(asignado, stock);
  const fecha = new Date().toLocaleString("es-MX");

  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  // =========================
  // PORTADA
  // =========================
  drawHeader(doc, "IP DEPOT · Plano Maestro de Inventario", `Generado: ${fecha}`);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(...BRAND.navy);
  doc.text("Plano Maestro de Inventario", 12, 34);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...BRAND.slate);
  doc.text(
    "Estructura enterprise: Proyecto → Municipio → Oficina → Estación/Módulo → Equipos. Incluye stock sin asignar.",
    12,
    42
  );

  drawKpi(doc, 12, 52, 38, 20, "Total equipos", totales.total);
  drawKpi(doc, 54, 52, 38, 20, "Asignados", totales.asignados);
  drawKpi(doc, 96, 52, 38, 20, "Stock", totales.stock);
  drawKpi(doc, 138, 52, 38, 20, "Proyectos", totales.proyectos);
  drawKpi(doc, 180, 52, 38, 20, "Municipios", totales.municipios);
  drawKpi(doc, 222, 52, 38, 20, "Oficinas", totales.oficinas);

  addSectionTitle(doc, "Resumen ejecutivo por proyecto", 86);

  table(doc, {
    startY: 91,
    head: [["Proyecto", "Municipios", "Oficinas", "Estaciones / módulos", "Equipos"]],
    body: resumenPorProyecto(asignado, stock),
    columnStyles: {
      0: { cellWidth: 110 },
      1: { halign: "center" },
      2: { halign: "center" },
      3: { halign: "center" },
      4: { halign: "center" },
    },
  });

  const yMunicipios = doc.lastAutoTable.finalY + 10;
  addSectionTitle(doc, "Resumen ejecutivo por municipio", yMunicipios);

  table(doc, {
    startY: yMunicipios + 5,
   head: [["Municipio", "Proyectos", "Oficinas físicas", "Estaciones / módulos", "Equipos"]],
    body: resumenPorMunicipio(asignado),
    columnStyles: {
      0: { cellWidth: 110 },
      1: { halign: "center" },
      2: { halign: "center" },
      3: { halign: "center" },
      4: { halign: "center" },
    },
  });

  // =========================
  // CUERPO
  // =========================
  Object.entries(asignado)
    .sort(([a], [b]) => a.localeCompare(b, "es"))
    .forEach(([proyecto, municipiosObj]) => {
      doc.addPage();
      drawHeader(doc, `PROYECTO: ${proyecto}`, "Inventario asignado por municipio, oficina y estación");

      let y = 28;

      Object.entries(municipiosObj)
        .sort(([a], [b]) => a.localeCompare(b, "es"))
        .forEach(([municipio, oficinasObj]) => {
          if (y > 178) {
            doc.addPage();
            drawHeader(doc, `PROYECTO: ${proyecto}`, "Continuación");
            y = 28;
          }

          doc.setFillColor(230, 236, 245);
          doc.roundedRect(10, y - 6, 277, 10, 2, 2, "F");

          doc.setFont("helvetica", "bold");
          doc.setFontSize(10);
          doc.setTextColor(...BRAND.navy);
          doc.text(`MUNICIPIO: ${municipio}`, 13, y);

          y += 9;

          Object.entries(oficinasObj)
            .sort(([a], [b]) => a.localeCompare(b, "es"))
            .forEach(([oficina, estacionesObj]) => {
              if (y > 178) {
                doc.addPage();
                drawHeader(doc, `PROYECTO: ${proyecto}`, "Continuación");
                y = 28;
              }

              const totalOficina = Object.values(estacionesObj).reduce(
                (acc, items) => acc + items.length,
                0
              );

              doc.setDrawColor(...BRAND.border);
              doc.setFillColor(248, 250, 252);
              doc.roundedRect(12, y - 5, 273, 9, 2, 2, "FD");

              doc.setFont("helvetica", "bold");
              doc.setFontSize(8);
              doc.setTextColor(31, 41, 55);
              doc.text(`Oficina: ${oficina}`, 15, y);

              doc.setFont("helvetica", "normal");
              doc.setTextColor(...BRAND.slate);
              doc.text(
                `Estaciones: ${Object.keys(estacionesObj).length} · Equipos: ${totalOficina}`,
                282,
                y,
                { align: "right" }
              );

              y += 8;

              Object.entries(estacionesObj)
                .sort(([a], [b]) => a.localeCompare(b, "es"))
                .forEach(([estacion, items]) => {
                  if (y > 170) {
                    doc.addPage();
                    drawHeader(doc, `PROYECTO: ${proyecto}`, "Continuación");
                    y = 28;
                  }

                  const evaluacion = evaluarEstacion(estacion, items);
                  const colorEval =
                    evaluacion.estatus === "COMPLETA" ? BRAND.green : BRAND.amber;

                  doc.setFillColor(241, 245, 249);
                  doc.roundedRect(15, y - 5, 267, 10, 2, 2, "F");

                  doc.setFont("helvetica", "bold");
                  doc.setFontSize(8);
                  doc.setTextColor(...BRAND.navy);
                  doc.text(`Estación / Módulo: ${estacion}`, 18, y);

                  doc.setTextColor(...colorEval);
                  doc.text(`Conformación: ${evaluacion.estatus}`, 170, y);

                  doc.setFont("helvetica", "normal");
                  doc.setTextColor(...BRAND.slate);
                  doc.text(`Equipos: ${items.length}`, 258, y);

                  y += 6;

                  doc.setFontSize(7);
                  doc.setTextColor(...BRAND.slate);
                  doc.text(`Faltantes / revisión: ${evaluacion.faltantes}`, 18, y);

                  y += 3;

                  table(doc, {
                    startY: y + 2,
                    margin: { left: 15, right: 15 },
                    head: [[
                      "ID",
                      "Equipo",
                      "Marca",
                      "Modelo",
                      "Serie",
                      "Estado",
                      "Notas",
                    ]],
                    body: items.map((item) => [
                      getId(item),
                      getEquipo(item),
                      getMarca(item),
                      getModelo(item),
                      getSerie(item),
                      getEstado(item),
                      getNotas(item),
                    ]),
                    columnStyles: {
                      0: { cellWidth: 18 },
                      1: { cellWidth: 38 },
                      2: { cellWidth: 28 },
                      3: { cellWidth: 38 },
                      4: { cellWidth: 38 },
                      5: { cellWidth: 28 },
                      6: { cellWidth: 70 },
                    },
                    didParseCell: (hookData) => {
                      if (hookData.section === "body" && hookData.column.index === 5) {
                        const color = estadoColor(hookData.cell.raw);
                        hookData.cell.styles.textColor = color;
                        hookData.cell.styles.fontStyle = "bold";
                      }
                    },
                  });

                  y = doc.lastAutoTable.finalY + 8;
                });

              y += 2;
            });

          y += 4;
        });
    });

  // =========================
  // STOCK
  // =========================
  doc.addPage();
  drawHeader(doc, "ANEXO A · Stock / Inventario sin asignar", "Equipos disponibles o con asignación incompleta");

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...BRAND.slate);
  doc.text(
    "Este anexo concentra equipos en stock, disponibles o registros sin proyecto/municipio/oficina/módulo completo.",
    12,
    28
  );

  table(doc, {
    startY: 36,
    head: [[
      "ID",
      "Equipo",
      "Marca",
      "Modelo",
      "Serie",
      "Estado",
      "Proyecto",
      "Municipio",
      "Oficina",
      "Módulo",
      "Notas",
    ]],
    body: stock.map((item) => [
      getId(item),
      getEquipo(item),
      getMarca(item),
      getModelo(item),
      getSerie(item),
      getEstado(item),
      getProyecto(item),
      getMunicipio(item) || "SIN ASIGNAR",
      getOficina(item) || "SIN ASIGNAR",
      getModulo(item) || "SIN ASIGNAR",
      getNotas(item),
    ]),
    columnStyles: {
      0: { cellWidth: 16 },
      1: { cellWidth: 28 },
      2: { cellWidth: 22 },
      3: { cellWidth: 28 },
      4: { cellWidth: 30 },
      5: { cellWidth: 20 },
      6: { cellWidth: 30 },
      7: { cellWidth: 26 },
      8: { cellWidth: 28 },
      9: { cellWidth: 24 },
      10: { cellWidth: 40 },
    },
    didParseCell: (hookData) => {
      if (hookData.section === "body" && hookData.column.index === 5) {
        const color = estadoColor(hookData.cell.raw);
        hookData.cell.styles.textColor = color;
        hookData.cell.styles.fontStyle = "bold";
      }
    },
  });

  drawFooter(doc);

  const fechaArchivo = new Date().toISOString().slice(0, 10);
  doc.save(`Plano_Maestro_Inventario_${fechaArchivo}.pdf`);
}